import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import busImg from "@/assets/fiaba-day-bus.jpg";
import busImgWebp from "@/assets/fiaba-day-bus.webp";
import { Award, BookOpen, Heart, Scale, CheckCircle2 } from "lucide-react";

const values = [
  { icon: Heart, t: "Accessibilità", d: "Spazi e servizi pensati per tutti, senza esclusioni." },
  { icon: Scale, t: "Inclusione", d: "Diritti uguali e partecipazione piena alla vita sociale." },
  { icon: BookOpen, t: "Competenza", d: "Esperienza tecnica e formazione continua del personale scolastico." },
  { icon: Award, t: "Riconoscimento", d: "Ente accreditato MIM, riconoscimento giuridico dal 2025." },
];

const osservatori = [
  "Osservatorio Nazionale sulla condizione delle persone con disabilità (Convenzione ONU)",
  "Consulta delle Associazioni dell'Osservatorio permanente per l'integrazione degli alunni con disabilità — MIM",
  "Commissione ministeriale per le problematiche relative alla disabilità nei beni e attività culturali",
  "Osservatorio Nazionale dell'Associazionismo",
  "Comitato per la Promozione e il Sostegno del Turismo Accessibile",
  "Gruppo di lavoro su barriere e accessibilità di navi e porti — MIT, Direzione Trasporto Marittimo",
  "Gruppo di lavoro UNI/CT 033/GL 32 — Accessibilità e fruibilità dell'ambiente costruito",
  "Tavolo delle Associazioni con ADR Aeroporti di Roma, RFI, ATAC e altri vettori",
];

const ChiSiamo = () => (
  <>
    <SEO
      title="Chi siamo — FIABA ETS"
      description="FIABA ETS, costituita nel 2000, è il Fondo Italiano per l'Abbattimento delle Barriere Architettoniche. Ente del Terzo Settore iscritto al RUNTS."
      path="/chi-siamo"
    />
    <PageHero
      eyebrow="Chi siamo"
      title="25 anni al servizio dell'accessibilità"
      subtitle="Dal 2000 lavoriamo affinché ogni persona possa muoversi liberamente e in autonomia."
    />

    <section className="section-padding">
      <div className="container-narrow grid lg:grid-cols-5 gap-12">
        <article className="lg:col-span-3 prose prose-lg max-w-none text-foreground/90 space-y-5 leading-relaxed">
          <p><strong>FIABA ETS</strong>, il Fondo Italiano per l'Abbattimento delle Barriere Architettoniche (già ONLUS), si è costituito con atto del Notaio Antonio Mosca il <strong>27 luglio 2000</strong>.</p>
          <p>È un Ente del Terzo Settore, iscritto al RUNTS nella sezione "Altri Enti del Terzo Settore" con determina n. G14936 del 10 novembre 2023, ed è dotato di riconoscimento giuridico con determina n. G07018 del 5 giugno 2025.</p>
          <p>FIABA nasce con l'obiettivo di promuovere, a livello nazionale, la sensibilizzazione sull'abbattimento delle barriere architettoniche fisiche e culturali, creando le condizioni affinché in futuro tutte le persone possano muoversi ovunque liberamente e in autonomia.</p>
          <p>Nel 2021 FIABA ha promosso l'emendamento relativo al <strong>"Bonus Barriere 75%"</strong>, introdotto dalla Legge di Bilancio 2022 e successivamente prorogato dalla Legge di Bilancio 2023 fino al 31 dicembre 2025.</p>
          <p>Da oltre 25 anni FIABA porta avanti campagne di sensibilizzazione affinché gli ambienti siano progettati in forma totalmente accessibile, secondo i principi dell'<strong>Universal Design</strong>, la "progettazione per tutti" che pone attenzione ai bisogni e ai desideri di ogni persona.</p>
          <p>Nel 2023 FIABA ha avviato un nuovo corso: è diventata ufficialmente Ente del Terzo Settore e ha visto l'avvicendamento alla Presidenza tra <strong>Giuseppe Trieste</strong>, oggi Presidente Emerito, e l'attuale Presidente <strong>Stefano Maiandi</strong>.</p>
          <p>Con decreto n. AOODPIT 784 del 1° agosto 2026, FIABA è stata inserita nell'elenco dei Soggetti accreditati e qualificati per la formazione del personale della scuola.</p>
          <p>FIABA aderisce inoltre al Tavolo delle Associazioni con aziende del settore trasporti e vettori, tra cui ADR Assistance – Aeroporti di Roma, RFI – Ferrovie dello Stato Italiane, ATAC e altri operatori del comparto.</p>
        </article>

        <aside className="lg:col-span-2 space-y-6">
          <picture>
            <source srcSet={busImgWebp} type="image/webp" />
            <img src={busImg} alt="FIABA Day — autobus istituzionale" className="rounded-lg shadow-elegant w-full" width={1024} height={1538} loading="lazy" decoding="async" />
          </picture>
          <div className="bg-muted p-6 rounded-lg border-l-4 border-secondary">
            <p className="font-serif text-xl text-primary font-bold mb-2">I nostri valori</p>
            <ul className="space-y-3 text-sm">
              {values.map((v) => (
                <li key={v.t} className="flex gap-3">
                  <v.icon className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                  <span><strong className="text-primary">{v.t}.</strong> <span className="text-muted-foreground">{v.d}</span></span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </section>

    {/* Cosa facciamo — Osservatori e Commissioni (dal sito originale fiaba.org) */}
    <section className="bg-subtle section-padding">
      <div className="container-narrow">
        <div className="max-w-3xl mb-10">
          <p className="uppercase tracking-widest text-secondary text-xs font-bold mb-3">Cosa facciamo</p>
          <h2 className="heading-lg text-primary mb-4">Osservatori, Consulte e Commissioni</h2>
          <p className="text-muted-foreground leading-relaxed">
            FIABA ETS partecipa attivamente ai principali tavoli istituzionali nazionali
            sull'accessibilità, la disabilità e l'Universal Design.
          </p>
        </div>
        <ul className="grid md:grid-cols-2 gap-4">
          {osservatori.map((o) => (
            <li key={o} className="flex gap-3 bg-card border border-border rounded-lg p-5 shadow-soft">
              <CheckCircle2 className="w-5 h-5 text-secondary shrink-0 mt-0.5" aria-hidden="true" />
              <span className="text-sm text-foreground/90 leading-relaxed">{o}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  </>
);

export default ChiSiamo;
